// Heading copy for the SSI advice step. This step makes no recommendations — it
// only reflects the user's current SSI configuration — so the message is chosen
// purely from what's included in planning and each person's start status.
//
// `receivingSsi` mirrors the SSI benefits model: 1 means "already receiving",
// 0 means a future start age (`paymentBeginAge`).
//
// Returns an array of `{ text, bold }` segments (like the retirement-age step) so
// the step can render the age(s) in bold within the otherwise-regular heading.

const SSI = 'Social Security Income (SSI)'

const alreadyReceiving = person => person.includeSsi && person.receivingSsi === 1

// Segment builders keep the branch copy readable.
const t = text => ({ text })
const age = value => ({ text: `${value}`, bold: true })

/**
 * Returns the heading segments for the SSI step.
 *
 * @param {boolean} spouseIncludedInPlanning whether the spouse is part of the plan
 * @param {{ includeSsi: boolean, receivingSsi: number, paymentBeginAge: number }} primary
 * @param {{ includeSsi: boolean, receivingSsi: number, paymentBeginAge: number }} [spouse]
 * @param {string} [spouseName] spouse's preferred name
 * @returns {Array<{ text: string, bold?: boolean }>}
 */
export function getHeadingSegments({ spouseIncludedInPlanning, primary, spouse, spouseName }) {
  // Single (or spouse excluded from planning): the message is about the participant only.
  if (!spouseIncludedInPlanning) {
    return soloSegments(primary)
  }

  const primaryIncluded = !!primary.includeSsi
  const spouseIncluded = !!spouse?.includeSsi

  // Neither person's SSI is included.
  if (!primaryIncluded && !spouseIncluded) {
    return [t(`You've chosen to exclude ${SSI} from planning.`)]
  }

  // Only the participant's SSI is included — same copy as the solo case.
  if (primaryIncluded && !spouseIncluded) {
    return soloSegments(primary)
  }

  // Only the spouse's SSI is included.
  if (!primaryIncluded && spouseIncluded) {
    return alreadyReceiving(spouse)
      ? [t(`${spouseName} is currently receiving ${SSI}.`)]
      : [
          t(`${spouseName} has chosen to start receiving ${SSI} at age `),
          age(spouse.paymentBeginAge),
          t('.'),
        ]
  }

  // Both people's SSI is included.
  const primaryReceiving = alreadyReceiving(primary)
  const spouseReceiving = alreadyReceiving(spouse)

  if (primaryReceiving && spouseReceiving) {
    return [t(`You and ${spouseName} are currently receiving ${SSI}.`)]
  }

  if (!primaryReceiving && !spouseReceiving) {
    return primary.paymentBeginAge === spouse.paymentBeginAge
      ? [
          t(`You and ${spouseName} have chosen to start receiving ${SSI} at age `),
          age(primary.paymentBeginAge),
          t('.'),
        ]
      : [
          t(`You've chosen to start receiving ${SSI} at age `),
          age(primary.paymentBeginAge),
          t(`, and ${spouseName} at `),
          age(spouse.paymentBeginAge),
          t('.'),
        ]
  }

  if (primaryReceiving && !spouseReceiving) {
    return [
      t(`You are currently receiving ${SSI}, and ${spouseName} has chosen to receive it at age `),
      age(spouse.paymentBeginAge),
      t('.'),
    ]
  }

  // primary future start, spouse already receiving
  return [
    t(`You've chosen to start receiving ${SSI} at age `),
    age(primary.paymentBeginAge),
    t(`, and ${spouseName} is currently receiving it.`),
  ]
}

// Segments when only the participant matters (single, spouse excluded, or only
// the participant's SSI is included).
function soloSegments(primary) {
  if (!primary.includeSsi) {
    return [t(`You've chosen to exclude ${SSI} from planning.`)]
  }
  return alreadyReceiving(primary)
    ? [t(`You're currently receiving ${SSI}.`)]
    : [t(`You've chosen to start receiving ${SSI} at age `), age(primary.paymentBeginAge), t('.')]
}
